"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { GoogleIcon } from "@/components/icons";
import { useLogin } from "@/hooks/auth/useLogin";
import { useI18n } from "@/lib/i18n";

export function useLoginModal() {
  const [open, setOpen] = useState(false);

  return {
    open,
    showLogin: () => setOpen(true),
    hideLogin: () => setOpen(false),
    setLoginOpen: setOpen,
  };
}

export function LoginModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const { isGoogleLoading, isAnyLoading, handleGoogleLogin } = useLogin();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px] rounded-2xl border-0 p-0 overflow-hidden"
        style={{
          background: "#0F172A",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
        }}
      >
        {/* Top accent */}
        <div
          className="h-1.5 w-full"
          style={{
            background: "linear-gradient(90deg, #06B6D4, #3B82F6, #818CF8)",
          }}
        />

        <div className="px-7 pt-6 pb-8">
          <DialogHeader className="text-center mb-6">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: "linear-gradient(135deg, #06B6D4, #3B82F6)",
              }}
            >
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <DialogTitle
              className="text-2xl font-extrabold"
              style={{ color: "#F1F5F9" }}
            >
              {t.login.welcomeBack}
            </DialogTitle>
            <DialogDescription
              className="text-sm mt-1"
              style={{ color: "#64748B" }}
            >
              {t.login.subtitle}
            </DialogDescription>
          </DialogHeader>

          <Button
            onClick={handleGoogleLogin}
            disabled={isAnyLoading}
            className="w-full h-13 cursor-pointer rounded-xl font-semibold text-base transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
            style={{
              background: "linear-gradient(135deg, #06B6D4, #3B82F6)",
              color: "#fff",
            }}
          >
            {isGoogleLoading ? (
              <Loader2 className="animate-spin w-5 h-5 mr-2" />
            ) : (
              <GoogleIcon />
            )}
            <span className="ml-2">{t.common.signInWithGoogle}</span>
          </Button>

          <p
            className="text-center text-[11px] mt-4"
            style={{ color: "#334155" }}
          >
            {t.common.bySigningIn}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
